import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IEffortActual } from "app/containers/Features/containers/Ticket/types";
import { IPagination, sortOrder } from "app/types";
import {
  IDashboardMyProject,
  IDashboardTicketCategory,
  IDashboardUserTicket,
  IMyTicketsFilters,
  IProjectDetails,
  IUserActivity,
  IUserActivityFilters,
} from "../type";
import { initialState } from "./initialState";
import {
  addOrUpdateEffortActualEntry,
  removeEffortActualEntry,
} from "app/containers/Features/helper";

const homeSlice = createSlice({
  name: "homeState",
  initialState,
  reducers: {
    getChartData: (state) => {
      state.isLoading = true;
    },
    setChartLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setMyProjects: (state, action: PayloadAction<IDashboardMyProject[]>) => {
      state.myProjects = action.payload;
    },
    setMyTickets: (
      state,
      action: PayloadAction<IDashboardTicketCategory[]>,
    ) => {
      state.myTickets = action.payload;
    },

    // My Tickets Section
    getMyTickets: (state) => {
      state.isTicketsLoading = true;
    },
    setMyTicketsList: (
      state,
      action: PayloadAction<IDashboardUserTicket[]>,
    ) => {
      if (state.ticketsPagination.page === 1) {
        state.myTicketsList = action.payload;
      } else {
        state.myTicketsList = [...state.myTicketsList, ...action.payload];
      }
    },

    updateMyTicketFieldsLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        fields: Partial<IDashboardUserTicket>;
      }>,
    ) => {
      const { ticketId, fields } = action.payload;
      state.myTicketsList = state.myTicketsList.map((ticket) =>
        ticket._id === ticketId ? { ...ticket, ...fields } : ticket,
      );
    },
    setTicketsPagination: (state, action: PayloadAction<IPagination>) => {
      state.ticketsPagination = action.payload;
    },
    setTicketsOrderBy: (state, action: PayloadAction<string>) => {
      state.ticketsPagination.orderBy = action.payload;
    },
    setTicketsOrderDirection: (state, action: PayloadAction<sortOrder>) => {
      state.ticketsPagination.orderDirection = action.payload;
    },
    setMyTicketsFilters: (state, action: PayloadAction<IMyTicketsFilters>) => {
      state.myTicketsFilters = action.payload;
    },
    setTicketsLoading: (state, action: PayloadAction<boolean>) => {
      state.isTicketsLoading = action.payload;
    },

    // Project Details
    getProjectDetails: (
      _state,
      _action: PayloadAction<{ projectIds: string[] }>,
    ) => {},
    setProjectDetails: (state, action: PayloadAction<IProjectDetails[]>) => {
      const existingIds = new Set(state.projectDetails.map((p) => p._id));
      const newProjects = action.payload.filter((p) => !existingIds.has(p._id));
      state.projectDetails.push(...newProjects);
    },

    // User Activities
    getUserActivities: (state) => {
      state.isActivitiesLoading = true;
    },
    setUserActivities: (state, action: PayloadAction<IUserActivity[]>) => {
      if (state.activitiesPagination.page === 1) {
        state.userActivities = action.payload;
      } else {
        state.userActivities = [...state.userActivities, ...action.payload];
      }
    },
    setActivitiesPagination: (state, action: PayloadAction<IPagination>) => {
      state.activitiesPagination = action.payload;
    },
    setActivitiesOrderBy: (state, action: PayloadAction<string>) => {
      state.activitiesPagination.orderBy = action.payload;
    },
    setActivitiesOrderDirection: (state, action: PayloadAction<sortOrder>) => {
      state.activitiesPagination.orderDirection = action.payload;
    },
    setActivityFilters: (
      state,
      action: PayloadAction<IUserActivityFilters>,
    ) => {
      state.activityFilters = action.payload;
    },
    setActivitiesLoading: (state, action: PayloadAction<boolean>) => {
      state.isActivitiesLoading = action.payload;
    },

    addEffortActualEntryLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        entry: IEffortActual;
      }>,
    ) => {
      const { ticketId, entry } = action.payload;
      state.myTicketsList = addOrUpdateEffortActualEntry(
        state.myTicketsList,
        ticketId,
        entry,
      );
    },

    updateEffortActualEntryLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        entryId: string;
        updates: Partial<IEffortActual>;
      }>,
    ) => {
      const { ticketId, entryId, updates } = action.payload;
      state.myTicketsList = addOrUpdateEffortActualEntry(
        state.myTicketsList,
        ticketId,
        updates,
        entryId,
      );
    },

    deleteEffortActualEntryLocally: (
      state,
      action: PayloadAction<{
        ticketId: string;
        effortId: string;
      }>,
    ) => {
      const { ticketId, effortId } = action.payload;
      state.myTicketsList = removeEffortActualEntry(
        state.myTicketsList,
        ticketId,
        effortId,
      );
    },

    resetHomeState: () => {
      return initialState;
    },
  },
});

export const { actions, reducer: homeReducer, name: homeSliceName } = homeSlice;
